import { Minus, Plus } from "lucide-react";
import { VIEW_LABELS } from "@/components/shirts/ApparelCanvas";
import { money } from "@/lib/signs";

const Label = ({ children, hint }) => (
    <div className="mb-1.5 flex items-baseline justify-between px-1">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{children}</p>
        {hint && <span className="text-[11px] text-zinc-500">{hint}</span>}
    </div>
);

/** Embroidery stitch tiers + digitizing, or screen-print ink colors per location with the quantity breaks. */
export default function DecorationPanel({ product, locations, deco, setDeco, quote, theme }) {
    const pricing = product.pricing || {};
    const detail = quote?.decoration;
    const accent = theme?.ink || theme?.primary;

    if (product.decoration === "embroidery") {
        const tiers = pricing.stitch_tiers || [];
        return (
            <div className="p-1" data-testid="decoration-panel">
                <Label hint="per location, per piece">Stitch count</Label>
                <div className="space-y-1.5">
                    {tiers.map((t) => {
                        const on = deco.stitch_tier === t.id;
                        return (
                            <button key={t.id} type="button" onClick={() => setDeco({ ...deco, stitch_tier: t.id })} data-testid={`stitch-tier-${t.id}`}
                                className="flex w-full items-center justify-between rounded-lg border bg-white px-3 py-2 text-left transition-colors hover:bg-zinc-50"
                                style={{ borderColor: on ? theme?.primary : "#e4e4e7" }}>
                                <span className={`text-sm ${on ? "font-bold text-zinc-900" : "text-zinc-700"}`}>{t.label}</span>
                                <span className="font-mono text-sm font-bold" style={{ color: accent }}>+{money(t.price)}</span>
                            </button>
                        );
                    })}
                </div>

                <label className="mt-2.5 flex cursor-pointer items-start gap-2.5 rounded-lg border border-zinc-200 bg-white p-3" data-testid="digitizing-row">
                    <input type="checkbox" className="mt-0.5 h-4 w-4" style={{ accentColor: theme?.primary }} data-testid="digitizing-toggle"
                        checked={deco.digitizing !== false} onChange={(e) => setDeco({ ...deco, digitizing: e.target.checked })} />
                    <span>
                        <span className="block text-sm font-bold text-zinc-900">Digitize my artwork — {money(pricing.digitizing_fee)} per design</span>
                        <span className="mt-0.5 block text-[11px] leading-snug text-zinc-500">One time. Uncheck it if you already have a stitch file (.dst / .emb).</span>
                    </span>
                </label>

                <p className="mt-2 px-1 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500" data-testid="deco-summary">
                    {locations.length ? `${locations.length} stitched location${locations.length > 1 ? "s" : ""}` : "Add artwork to a location"}
                    {detail?.setup ? ` · ${money(quote.setup_fee)} setup` : ""}
                </p>
            </div>
        );
    }

    const cap = pricing.max_colors || 6;
    const colors = deco.colors || {};
    const bump = (view, delta) => {
        const next = Math.min(Math.max((Number(colors[view]) || 1) + delta, 1), cap);
        setDeco({ ...deco, colors: { ...colors, [view]: next } });
    };
    const btn = "grid h-6 w-6 place-items-center rounded-full border border-zinc-300 text-zinc-700 hover:border-zinc-500";

    return (
        <div className="p-1" data-testid="decoration-panel">
            <Label hint={`${cap} colors max per location`}>Ink colors</Label>
            {!locations.length && <p className="px-1 text-[11px] text-zinc-500">Upload artwork first — then set how many ink colors each side needs.</p>}
            <div className="space-y-1.5">
                {locations.map((v) => (
                    <div key={v} className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white px-3 py-2" data-testid={`ink-row-${v}`}>
                        <span className="text-sm font-semibold text-zinc-900">{VIEW_LABELS[v] || v}</span>
                        <div className="flex items-center gap-1.5">
                            <button type="button" className={btn} onClick={() => bump(v, -1)} data-testid={`ink-minus-${v}`}><Minus size={11} /></button>
                            <span className="w-16 text-center font-mono text-sm text-zinc-900" data-testid={`ink-count-${v}`}>
                                {Number(colors[v]) || 1} color{(Number(colors[v]) || 1) > 1 ? "s" : ""}
                            </span>
                            <button type="button" className={btn} onClick={() => bump(v, 1)} data-testid={`ink-plus-${v}`}><Plus size={11} /></button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-2.5 rounded-lg border border-zinc-200 bg-white p-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">Quantity breaks · per color, per piece</p>
                <div className="mt-1.5 space-y-0.5">
                    {(pricing.qty_tiers || []).map((t) => {
                        const on = detail?.qty_tier === t.min_qty;
                        return (
                            <div key={t.min_qty} className={`flex items-center justify-between text-xs ${on ? "font-bold" : "text-zinc-500"}`}
                                style={on ? { color: accent } : undefined} data-testid={`qty-tier-${t.min_qty}`}>
                                <span>{t.min_qty === 1 ? "1 – 23 pcs" : `${t.min_qty}+ pcs`}</span>
                                <span className="font-mono">{money(t.per_color)}</span>
                            </div>
                        );
                    })}
                </div>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500" data-testid="deco-summary">
                    {detail?.screens ? `${detail.screens} screen${detail.screens > 1 ? "s" : ""} · ${money(quote.setup_fee)} setup` : `${money(pricing.screen_fee)} per screen setup`}
                </p>
            </div>
        </div>
    );
}
